// Reprise hors connexion des prospects : la saisie est gardée sur le téléphone (IndexedDB) puis renvoyée à l'ouverture de l'application
// et au retour du réseau. Pas de synchronisation en arrière-plan (peu fiable sur iPhone) ; la signature d'un contrat exige le réseau.
// Si IndexedDB est indisponible (navigation privée, vieux navigateur), tout reste en mémoire pour la session : rien ne plante.

import type { Prospect, SaisieProspect } from './prospectsApi';

export interface OperationEnAttente { id: string; par: string; type: 'enregistrer' | 'supprimer'; saisie?: SaisieProspect; le: string }
interface ListeGardee { cle: 'liste'; prospects: Prospect[]; quartiers: string[]; le: string }

const NOM_BASE = 'iamani-prospects';
const memoire = { file: new Map<string, OperationEnAttente>(), liste: null as ListeGardee | null };

let ouverture: Promise<IDBDatabase | null> | null = null;
const ouvrir = (): Promise<IDBDatabase | null> => {
    if (ouverture) return ouverture;
    ouverture = new Promise((resolve) => {
        try {
            if (typeof indexedDB === 'undefined') { resolve(null); return; }
            const req = indexedDB.open(NOM_BASE, 1);
            req.onupgradeneeded = () => {
                req.result.createObjectStore('file', { keyPath: 'id' });
                req.result.createObjectStore('cache', { keyPath: 'cle' });
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(null);
            req.onblocked = () => resolve(null);
        } catch { resolve(null); }
    });
    return ouverture;
};

function demander<T>(db: IDBDatabase, magasin: string, mode: IDBTransactionMode, action: (s: IDBObjectStore) => IDBRequest): Promise<T | null> {
    return new Promise((resolve) => {
        try {
            const tx = db.transaction(magasin, mode);
            const req = action(tx.objectStore(magasin));
            req.onsuccess = () => resolve((req.result ?? null) as T | null);
            req.onerror = () => resolve(null);
            tx.onabort = () => resolve(null);
        } catch { resolve(null); }
    });
}

/** Une saisie ou une suppression par fiche : la dernière remplace la précédente (une seule visite = une seule ligne à renvoyer). */
export const mettreEnAttente = async (op: OperationEnAttente) => {
    const db = await ouvrir();
    const precedente = db ? await demander<OperationEnAttente>(db, 'file', 'readonly', (s) => s.get(op.id)) : memoire.file.get(op.id) || null;
    // Une fiche créée hors connexion puis supprimée avant tout envoi n'a jamais existé côté serveur : rien à renvoyer.
    if (op.type === 'supprimer' && precedente?.type === 'enregistrer' && precedente.saisie?.cree_le) {
        if (db) await demander(db, 'file', 'readwrite', (s) => s.delete(op.id)); else memoire.file.delete(op.id);
        return;
    }
    const fusion: OperationEnAttente = op.type === 'enregistrer' && precedente?.type === 'enregistrer'
        ? { ...op, saisie: { ...precedente.saisie, ...op.saisie, id: op.id, cree_le: precedente.saisie?.cree_le ?? op.saisie?.cree_le } as SaisieProspect }
        : op;
    if (db) await demander(db, 'file', 'readwrite', (s) => s.put(fusion)); else memoire.file.set(op.id, fusion);
};

/** Seules les saisies de la personne connectée sont renvoyées : sur un téléphone partagé, celles d'une autre restent en attente. */
export const lireFile = async (par: string): Promise<OperationEnAttente[]> => {
    const db = await ouvrir();
    const toutes = db ? await demander<OperationEnAttente[]>(db, 'file', 'readonly', (s) => s.getAll()) : [...memoire.file.values()];
    return (toutes || []).filter((o) => o.par === par).sort((a, b) => a.le.localeCompare(b.le));
};

export const retirerDeLaFile = async (id: string) => {
    const db = await ouvrir();
    if (!db) { memoire.file.delete(id); return; }
    await demander(db, 'file', 'readwrite', (s) => s.delete(id));
};

/** Dernière liste reçue, pour consulter ses fiches sans réseau. Elle contient des noms et des numéros : effacée à la déconnexion. */
export const garderListe = async (prospects: Prospect[], quartiers: string[]) => {
    const valeur: ListeGardee = { cle: 'liste', prospects, quartiers, le: new Date().toISOString() };
    const db = await ouvrir();
    if (!db) { memoire.liste = valeur; return; }
    await demander(db, 'cache', 'readwrite', (s) => s.put(valeur));
};

export const lireListeGardee = async (): Promise<ListeGardee | null> => {
    const db = await ouvrir();
    if (!db) return memoire.liste;
    return demander<ListeGardee>(db, 'cache', 'readonly', (s) => s.get('liste'));
};

/** À la déconnexion : la liste gardée (noms, numéros) est effacée ; les saisies pas encore envoyées restent, pour leur auteur. */
export const viderListeGardee = async () => {
    memoire.liste = null;
    const db = await ouvrir();
    if (!db) return;
    await demander(db, 'cache', 'readwrite', (s) => s.clear());
};
